import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { neutral, semantic, shadows, radii } from './tokens';

/**
 * MUI theme for the AWS Monitor design system.
 *
 * This file is the highest-leverage part of the redesign: every MUI component in
 * every feature picks these up without being touched, which is what makes the
 * whole app read as one product rather than eleven separately-styled pages.
 *
 * Because of that reach, the component overrides below are deliberately narrow.
 * A blanket `MuiPaper.root` shadow would also repaint every Menu, Select popover,
 * Autocomplete dropdown and Dialog — so elevation is mapped per level instead,
 * leaving the floating surfaces (elevation 8+) with the lift they need.
 */

const baseTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: semantic.primary.main,
      light: semantic.primary.light,
      dark: semantic.primary.dark,
      contrastText: '#ffffff',
    },
    secondary: {
      main: semantic.accent.main,
      light: semantic.accent.light,
      dark: semantic.accent.dark,
      contrastText: '#ffffff',
    },
    error: {
      main: semantic.error.text,
      light: semantic.error.light,
      dark: semantic.error.dark,
      contrastText: '#ffffff',
    },
    warning: {
      // The *text* step, not the vibrant one: MUI uses `warning.main` for chip
      // and alert labels, and #f59e0b on white is only 2.15:1.
      main: semantic.warning.text,
      light: semantic.warning.light,
      dark: semantic.warning.dark,
      contrastText: '#ffffff',
    },
    info: {
      main: semantic.info.text,
      light: semantic.info.light,
      dark: semantic.info.dark,
      contrastText: '#ffffff',
    },
    success: {
      main: semantic.success.text,
      light: semantic.success.light,
      dark: semantic.success.dark,
      contrastText: '#ffffff',
    },
    grey: {
      50: neutral[50],
      100: neutral[100],
      200: neutral[200],
      300: neutral[300],
      400: neutral[400],
      500: neutral[500],
      600: neutral[600],
      700: neutral[700],
      800: neutral[800],
      900: neutral[900],
    },
    background: {
      default: neutral[50],
      paper: neutral[0],
    },
    text: {
      primary: neutral[900],
      secondary: neutral[600],
      disabled: neutral[400],
    },
    divider: neutral[200],
    action: {
      hover: 'rgba(37, 99, 235, 0.04)',
      selected: 'rgba(37, 99, 235, 0.08)',
      focus: 'rgba(37, 99, 235, 0.12)',
    },
  },

  typography: {
    fontFamily:
      '"Inter", "Inter var", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 700, fontSize: '2.25rem', letterSpacing: '-0.025em', lineHeight: 1.2 },
    h2: { fontWeight: 700, fontSize: '1.875rem', letterSpacing: '-0.022em', lineHeight: 1.25 },
    h3: { fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.02em', lineHeight: 1.3 },
    h4: { fontWeight: 700, fontSize: '1.375rem', letterSpacing: '-0.018em', lineHeight: 1.3 },
    h5: { fontWeight: 600, fontSize: '1.125rem', letterSpacing: '-0.012em', lineHeight: 1.4 },
    h6: { fontWeight: 600, fontSize: '1rem', letterSpacing: '-0.008em', lineHeight: 1.4 },
    subtitle1: { fontWeight: 600, fontSize: '0.9375rem', lineHeight: 1.5 },
    subtitle2: { fontWeight: 600, fontSize: '0.8125rem', lineHeight: 1.5 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.55 },
    caption: { fontSize: '0.75rem', lineHeight: 1.45, letterSpacing: '0.005em' },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      lineHeight: 1.4,
    },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
  },

  shape: { borderRadius: radii.md },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: neutral[50],
          color: neutral[900],
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        // Tabular figures wherever numbers stack vertically.
        'th, td': { fontVariantNumeric: 'tabular-nums' },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: radii.lg,
        },
        // Map MUI's elevation scale onto the token shadow ramp. Menus, Selects
        // and Autocompletes default to elevation 8, so they keep real lift.
        elevation0: { boxShadow: 'none' },
        elevation1: { boxShadow: shadows.sm },
        elevation2: { boxShadow: shadows.md },
        elevation3: { boxShadow: shadows.md },
        elevation4: { boxShadow: shadows.lg },
        elevation8: { boxShadow: shadows.lg },
        elevation16: { boxShadow: shadows.xl },
        elevation24: { boxShadow: shadows.xl },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${neutral[200]}`,
          borderRadius: radii.lg,
          boxShadow: shadows.sm,
          transition: 'box-shadow .18s ease, border-color .18s ease, transform .18s ease',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { padding: '18px 20px 6px' },
        title: { fontSize: '0.9375rem', fontWeight: 700, letterSpacing: '-0.008em' },
        subheader: { fontSize: '0.8125rem', color: neutral[500] },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: '16px 20px', '&:last-child': { paddingBottom: 20 } },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          padding: '8px 16px',
          fontWeight: 600,
          transition: 'background-color .16s ease, box-shadow .16s ease, border-color .16s ease',
        },
        sizeSmall: { padding: '5px 12px', fontSize: '0.8125rem' },
        sizeLarge: { padding: '11px 22px', fontSize: '0.9375rem' },
        contained: {
          boxShadow: shadows.xs,
          '&:hover': { boxShadow: shadows.md },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${semantic.primary.main} 0%, ${semantic.primary.dark} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${semantic.primary.dark} 0%, #1a44c2 100%)`,
          },
        },
        outlined: {
          borderColor: neutral[300],
          color: neutral[700],
          backgroundColor: neutral[0],
          '&:hover': {
            borderColor: semantic.primary.main,
            backgroundColor: semantic.primary.tint,
            color: semantic.primary.text,
          },
        },
        text: { '&:hover': { backgroundColor: neutral[100] } },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          transition: 'background-color .16s ease, color .16s ease',
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radii.sm, fontWeight: 600, fontSize: '0.75rem' },
        sizeSmall: { height: 22 },
        label: { paddingLeft: 8, paddingRight: 8 },
        outlined: { borderColor: neutral[200] },
        icon: { fontSize: '0.9375rem', marginLeft: 6 },
      },
    },

    MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radii.md,
          backgroundColor: neutral[0],
          '& .MuiOutlinedInput-notchedOutline': { borderColor: neutral[300] },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: neutral[400] },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: semantic.primary.main,
            borderWidth: 1,
          },
          '&.Mui-focused': { boxShadow: `0 0 0 3px ${semantic.primary.tint}` },
        },
        input: { fontSize: '0.875rem' },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { fontSize: '0.875rem' } } },
    MuiSelect: { styleOverrides: { select: { fontSize: '0.875rem' } } },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: radii.md,
          border: `1px solid ${neutral[200]}`,
          marginTop: 4,
        },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: radii.sm,
          fontSize: '0.875rem',
          minHeight: 38,
          '&.Mui-selected': {
            backgroundColor: semantic.primary.tint,
            color: semantic.primary.text,
            '&:hover': { backgroundColor: semantic.primary.tint },
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: neutral[800],
          borderRadius: radii.sm,
          fontSize: '0.75rem',
          fontWeight: 500,
          padding: '6px 10px',
          boxShadow: shadows.lg,
        },
        arrow: { color: neutral[800] },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44 },
        indicator: { height: 3, borderRadius: '3px 3px 0 0' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 44,
          fontWeight: 600,
          fontSize: '0.875rem',
          color: neutral[500],
          '&.Mui-selected': { color: semantic.primary.text },
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: neutral[50],
            color: neutral[600],
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
            borderBottom: `1px solid ${neutral[200]}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: neutral[100], fontSize: '0.875rem' },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: neutral[25] } },
      },
    },

    MuiDialog: {
      styleOverrides: { paper: { borderRadius: radii.xl } },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontSize: '1.0625rem', fontWeight: 700, padding: '20px 24px 8px' } },
    },
    MuiDialogContent: { styleOverrides: { root: { padding: '8px 24px' } } },
    MuiDialogActions: { styleOverrides: { root: { padding: '16px 24px 20px', gap: 8 } } },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: radii.md, fontSize: '0.875rem', border: '1px solid transparent' },
        standardInfo: { backgroundColor: semantic.info.tint, borderColor: semantic.info.border, color: semantic.info.text },
        standardSuccess: { backgroundColor: semantic.success.tint, borderColor: semantic.success.border, color: semantic.success.text },
        standardWarning: { backgroundColor: semantic.warning.tint, borderColor: semantic.warning.border, color: semantic.warning.text },
        standardError: { backgroundColor: semantic.error.tint, borderColor: semantic.error.border, color: semantic.error.text },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: radii.pill, height: 6, backgroundColor: neutral[100] },
        bar: { borderRadius: radii.pill },
      },
    },

    MuiDivider: { styleOverrides: { root: { borderColor: neutral[200] } } },

    MuiAvatar: { styleOverrides: { root: { fontWeight: 600, fontSize: '0.875rem' } } },

    MuiDrawer: { styleOverrides: { paper: { backgroundImage: 'none', border: 'none' } } },

    MuiSwitch: {
      styleOverrides: {
        root: { padding: 8 },
        track: { borderRadius: radii.pill, backgroundColor: neutral[300], opacity: 1 },
      },
    },

    MuiSkeleton: {
      styleOverrides: { root: { backgroundColor: neutral[100], borderRadius: radii.sm } },
    },

    // The DataGrid is the workhorse of this app — six pages render one.
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          fontSize: '0.875rem',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: neutral[50],
            borderBottom: `1px solid ${neutral[200]}`,
            minHeight: '46px !important',
            maxHeight: '46px !important',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
            color: neutral[600],
          },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${neutral[100]}`,
            '&:focus, &:focus-within': { outline: 'none' },
          },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-row': {
            transition: 'background-color .14s ease',
            '&:hover': { backgroundColor: neutral[25] },
            '&.Mui-selected': {
              backgroundColor: semantic.primary.tint,
              '&:hover': { backgroundColor: semantic.primary.tint },
            },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${neutral[200]}`,
            minHeight: 48,
          },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: neutral[0] },
          '& .MuiDataGrid-overlay': { backgroundColor: 'transparent' },
        },
      },
    },
  } as any,
});

export const theme = responsiveFontSizes(baseTheme);

/**
 * Retained for API compatibility — nothing imports it today, but removing an
 * exported symbol is the kind of "harmless cleanup" that breaks a build later.
 */
export const darkTheme = responsiveFontSizes(
  createTheme({
    ...baseTheme,
    palette: {
      ...baseTheme.palette,
      mode: 'dark',
      background: { default: neutral[950], paper: neutral[900] },
      text: { primary: '#ffffff', secondary: neutral[400], disabled: neutral[600] },
      divider: 'rgba(148,163,184,0.16)',
    },
  })
);

export default theme;
